import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield } from "lucide-react";

const PrivacyPage = () => {
  const { i18n, t } = useTranslation();
  const showEnDisclaimer = (i18n.language || "").startsWith("en");
  return (
    <div className="min-h-screen py-20 px-6">
      <div className="container mx-auto max-w-4xl">

        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-10 h-10 text-gold" />

            <h1 className="font-display text-4xl md:text-5xl text-gold">
              {showEnDisclaimer ? "Privacy Policy" : "Privacy Policy"}
            </h1>
          </div>

          <p className="text-muted-foreground">
            {showEnDisclaimer ? "Last updated: 08/11/2026" : "Ultimo aggiornamento: 11/08/2026"}
          </p>

          {showEnDisclaimer && (
            <div className="mt-6 p-4 rune-border bg-gold/10 border border-gold rounded-lg" data-testid="legal-en-notice">
              <p className="font-heading text-sm text-gold mb-2">
                {t("legal.onlyItalianTitle")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("legal.onlyItalianBody")}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-8 text-muted-foreground leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              1. Introduzione
            </h2>

            <p>
              Mytharion.app ("Mytharion", "noi", "ci" o "nostro") rispetta
              la privacy degli utenti e si impegna a proteggere i dati
              personali trattati attraverso il sito web mytharion.app e
              i relativi servizi (il "Servizio").
            </p>

            <p className="mt-3">
              La presente Privacy Policy descrive quali dati personali
              raccogliamo, per quali finalità li utilizziamo, con quali
              soggetti possono essere condivisi e quali diritti spettano
              agli utenti ai sensi del Regolamento (UE) 2016/679
              ("GDPR") e della normativa applicabile in materia di
              protezione dei dati personali.
            </p>

            <p className="mt-3">
              Le informazioni sono fornite in conformità agli articoli
              12, 13 e 14 del GDPR e sono destinate agli utenti del
              Servizio.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              2. Titolare del Trattamento
            </h2>

            <p>
              Il titolare del trattamento dei dati personali trattati
              nell'ambito del Servizio è:
            </p>

            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>Titolare del trattamento:</strong> Mytharion.app
              </li>

              <li>
                <strong>Sede:</strong> Napoli, Italia
              </li>

              <li>
                <strong>Email per questioni relative alla privacy:</strong>{" "}
                <a
                  href="mailto:privacy@mytharion.app"
                  className="text-gold hover:underline"
                >
                  privacy@mytharion.app
                </a>
              </li>
            </ul>

            <p className="mt-3">
              Mytharion.app è un'impresa gestita da una sola persona.
              Per le comunicazioni relative alla protezione dei dati
              personali viene utilizzato esclusivamente il contatto
              privacy indicato nella presente Policy.
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              3. Dati Personali Raccolti
            </h2>

            <p className="mb-3">
              A seconda delle funzionalità utilizzate, Mytharion può
              trattare le seguenti categorie di dati personali:
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Dati dell'account:</strong> nome, indirizzo email,
                immagine del profilo/avatar e informazioni associate
                all'autenticazione tramite Google OAuth.
              </li>

              <li>
                <strong>Dati relativi agli acquisti:</strong> prodotti
                acquistati, licenze, cronologia degli ordini, stato degli
                acquisti e informazioni relative agli abbonamenti.
              </li>

              <li>
                <strong>Dati relativi ai pagamenti:</strong> le informazioni
                necessarie per verificare e gestire i pagamenti possono
                essere trattate tramite Stripe e PayPal. Mytharion non
                memorizza direttamente i dati completi delle carte di
                pagamento.
              </li>

              <li>
                <strong>Dati tecnici:</strong> indirizzo IP, informazioni
                relative al browser, dispositivo, sistema operativo,
                informazioni di connessione e dati tecnici necessari al
                funzionamento e alla sicurezza del Servizio.
              </li>

              <li>
                <strong>Cookie e tecnologie simili:</strong> informazioni
                relative all'utilizzo dei cookie e delle tecnologie
                necessarie al funzionamento del sito, secondo quanto
                descritto nella Cookie Policy.
              </li>

              <li>
                <strong>Dati di utilizzo:</strong> informazioni relative
                alle pagine visitate, alle funzionalità utilizzate e alle
                interazioni con il Servizio, quando tecnicamente e
                legittimamente raccolte.
              </li>

              <li>
                <strong>Comunicazioni con il supporto:</strong> informazioni
                contenute nelle richieste di assistenza e nei ticket inviati
                dall'utente, comprese le informazioni necessarie per
                identificare e gestire la richiesta.
              </li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              4. Finalità del Trattamento
            </h2>

            <p className="mb-3">
              I dati personali possono essere trattati per le seguenti
              finalità:
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>
                creare, gestire e autenticare gli account degli utenti;
              </li>

              <li>
                fornire le funzionalità del Servizio;
              </li>

              <li>
                gestire acquisti, licenze, abbonamenti e relativi rinnovi;
              </li>

              <li>
                verificare e gestire i pagamenti;
              </li>

              <li>
                fornire assistenza e rispondere alle richieste degli utenti;
              </li>

              <li>
                inviare comunicazioni necessarie alla gestione del Servizio,
                quali conferme d'ordine, informazioni relative alle licenze,
                notifiche tecniche e comunicazioni amministrative;
              </li>

              <li>
                prevenire frodi, abusi e utilizzi non autorizzati;
              </li>

              <li>
                proteggere la sicurezza del Servizio, degli utenti e dei
                sistemi informatici;
              </li>

              <li>
                adempiere agli obblighi previsti dalla legge;
              </li>

              <li>
                esercitare o difendere diritti in sede giudiziaria;
              </li>

              <li>
                migliorare il Servizio e sviluppare nuove funzionalità,
                nei limiti consentiti dalla normativa applicabile.
              </li>
            </ul>
          </section>

          {/* 5 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              5. Basi Giuridiche del Trattamento
            </h2>

            <p className="mb-3">
              Il trattamento dei dati personali viene effettuato sulla base
              di una o più delle seguenti condizioni di liceità previste
              dall'articolo 6 del GDPR:
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Esecuzione del contratto:</strong> per creare e
                gestire l'account, fornire il Servizio, gestire acquisti,
                licenze e abbonamenti.
              </li>

              <li>
                <strong>Adempimento di obblighi legali:</strong> per
                rispettare obblighi fiscali, contabili, amministrativi o
                altri obblighi previsti dalla legge.
              </li>

              <li>
                <strong>Legittimo interesse:</strong> per finalità quali
                sicurezza, prevenzione delle frodi, protezione dei sistemi,
                gestione del Servizio e tutela dei diritti di Mytharion,
                nel rispetto del bilanciamento con i diritti e le libertà
                degli interessati.
              </li>

              <li>
                <strong>Consenso:</strong> quando il trattamento richiede
                il consenso dell'utente ai sensi della normativa applicabile.
              </li>
            </ul>
          </section>

          {/* 6 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              6. Fornitori e Condivisione dei Dati
            </h2>

            <p className="mb-3">
              I dati personali possono essere trattati da fornitori e
              partner che forniscono servizi necessari al funzionamento
              di Mytharion.
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Google:</strong> per l'autenticazione tramite Google
                OAuth e le relative funzionalità.
              </li>

              <li>
                <strong>Stripe:</strong> per la gestione dei pagamenti,
                degli acquisti e degli eventuali abbonamenti.
              </li>

              <li>
                <strong>PayPal:</strong> per la gestione dei pagamenti
                effettuati tramite PayPal.
              </li>

              <li>
                <strong>Cloudflare:</strong> per servizi infrastrutturali,
                di rete, sicurezza, protezione e distribuzione del traffico,
                quando applicabile al Servizio.
              </li>

              <li>
                <strong>Fornitori tecnici:</strong> soggetti che forniscono
                servizi di hosting, infrastruttura, email, assistenza,
                sicurezza o altri servizi necessari alla gestione del
                Servizio.
              </li>

              <li>
                <strong>Autorità pubbliche:</strong> quando la comunicazione
                dei dati è richiesta da un obbligo legale o da un ordine
                legittimo dell'autorità competente.
              </li>
            </ul>

            <p className="mt-3">
              I fornitori che trattano dati personali per conto di
              Mytharion sono selezionati, ove applicabile, nel rispetto
              degli obblighi previsti dal GDPR e possono agire in qualità
              di responsabili del trattamento o di autonomi titolari,
              a seconda della natura del servizio fornito.
            </p>

            <p className="mt-3">
              Mytharion non vende i dati personali degli utenti a terze
              parti.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              7. Trasferimenti Internazionali di Dati
            </h2>

            <p>
              Alcuni fornitori utilizzati da Mytharion possono trattare
              dati personali al di fuori dello Spazio Economico Europeo
              ("SEE").
            </p>

            <p className="mt-3">
              Quando un trattamento comporta un trasferimento di dati
              personali verso un Paese terzo, Mytharion adotta, ove
              applicabile, le misure e le garanzie previste dal GDPR,
              inclusa una decisione di adeguatezza della Commissione
              Europea oppure altre garanzie appropriate previste dagli
              articoli 44 e seguenti del GDPR.
            </p>

            <p className="mt-3">
              Le condizioni applicabili ai singoli fornitori possono essere
              ulteriormente descritte nelle rispettive privacy policy.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              8. I Tuoi Diritti ai Sensi del GDPR
            </h2>

            <p className="mb-3">
              In presenza dei presupposti previsti dalla normativa,
              l'utente può esercitare i seguenti diritti:
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Accesso:</strong> ottenere conferma dell'esistenza
                di un trattamento e una copia dei propri dati personali.
              </li>

              <li>
                <strong>Rettifica:</strong> ottenere la correzione dei dati
                personali inesatti o l'integrazione di quelli incompleti.
              </li>

              <li>
                <strong>Cancellazione (diritto all'oblio):</strong> richiedere
                la cancellazione dei dati personali nei casi previsti
                dall'articolo 17 del GDPR.
              </li>

              <li>
                <strong>Limitazione:</strong> richiedere la limitazione del
                trattamento nei casi previsti dall'articolo 18 del GDPR.
              </li>

              <li>
                <strong>Portabilità:</strong> ricevere i dati personali
                forniti a Mytharion in un formato strutturato, di uso comune
                e leggibile da dispositivo automatico, nei casi previsti
                dall'articolo 20 del GDPR.
              </li>

              <li>
                <strong>Opposizione:</strong> opporsi al trattamento nei
                casi previsti dall'articolo 21 del GDPR.
              </li>

              <li>
                <strong>Revoca del consenso:</strong> revocare il consenso
                precedentemente prestato, quando il trattamento si basa
                sul consenso. La revoca non pregiudica la liceità del
                trattamento effettuato prima della revoca.
              </li>
            </ul>
          </section>

          {/* 9 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              9. Richieste di Cancellazione e Diritto all'Oblio
            </h2>

            <p>
              L'utente può richiedere la cancellazione del proprio account
              e dei propri dati personali tramite una richiesta di
              assistenza inviata attraverso il sistema di ticket disponibile
              sul sito oppure contattando:
            </p>

            <p className="mt-3">
              <a
                href="mailto:privacy@mytharion.app"
                className="text-gold hover:underline"
              >
                privacy@mytharion.app
              </a>
            </p>

            <p className="mt-3">
              La richiesta sarà valutata ai sensi dell'articolo 17 del GDPR.
              Quando sussistono i presupposti previsti dalla normativa,
              Mytharion procederà alla cancellazione dei dati personali
              senza ingiustificato ritardo.
            </p>

            <p className="mt-3">
              La cancellazione non comporta necessariamente la rimozione
              assoluta di ogni informazione. Alcuni dati possono essere
              conservati quando ciò sia necessario per adempiere a obblighi
              legali, fiscali o contabili, oppure per l'accertamento,
              l'esercizio o la difesa di diritti in sede giudiziaria.
            </p>

            <p className="mt-3">
              La richiesta tramite ticket costituisce il canale operativo
              messo a disposizione da Mytharion per facilitare l'esercizio
              dei diritti dell'interessato.
            </p>

            <p className="mt-3">
              Mytharion risponderà alle richieste relative ai diritti
              dell'interessato senza ingiustificato ritardo e, in linea
              generale, entro un mese dalla ricezione della richiesta.
              Tale termine può essere prorogato nei casi e alle condizioni
              previste dall'articolo 12 del GDPR.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              10. Verifica dell'Identità
            </h2>

            <p>
              Per proteggere i dati personali degli utenti, Mytharion può
              richiedere informazioni aggiuntive ragionevolmente necessarie
              per verificare l'identità del soggetto che presenta una
              richiesta relativa ai diritti previsti dagli articoli 15-22
              del GDPR.
            </p>

            <p className="mt-3">
              Tali informazioni saranno utilizzate esclusivamente nella
              misura necessaria per verificare l'identità e gestire la
              richiesta.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              11. Conservazione dei Dati
            </h2>

            <p>
              Mytharion conserva i dati personali per il periodo necessario
              a raggiungere le finalità per cui sono stati raccolti e,
              successivamente, per il periodo eventualmente necessario a
              rispettare obblighi legali o tutelare i propri diritti.
            </p>

            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>Dati dell'account:</strong> generalmente per tutta
                la durata dell'account, salvo cancellazione o diverso
                obbligo di conservazione.
              </li>

              <li>
                <strong>Dati relativi agli acquisti:</strong> per il periodo
                necessario alla gestione dell'acquisto, della licenza e
                degli obblighi legali, fiscali e contabili applicabili.
              </li>

              <li>
                <strong>Dati relativi alle licenze:</strong> per il periodo
                necessario alla gestione e alla verifica dei diritti di
                utilizzo dei prodotti acquistati.
              </li>

              <li>
                <strong>Dati di assistenza:</strong> per il periodo necessario
                a gestire la richiesta e, ove necessario, per documentare
                la gestione della richiesta o tutelare diritti.
              </li>

              <li>
                <strong>Dati tecnici e di sicurezza:</strong> per il periodo
                necessario a garantire la sicurezza, diagnosticare problemi
                e prevenire abusi, nel rispetto dei principi di necessità e
                minimizzazione.
              </li>
            </ul>

            <p className="mt-3">
              In caso di cancellazione dell'account, i dati che non devono
              essere conservati per obbligo legale o per altre basi giuridiche
              applicabili saranno eliminati o resi non identificabili entro
              un periodo ragionevole, compatibilmente con le procedure
              tecniche e operative del Servizio.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              12. Cookie
            </h2>

            <p>
              Mytharion utilizza cookie e tecnologie simili per consentire
              il funzionamento del sito, mantenere l'autenticazione,
              gestire funzionalità essenziali e, ove applicabile, fornire
              ulteriori funzionalità.
            </p>

            <p className="mt-3">
              I cookie non strettamente necessari vengono utilizzati nel
              rispetto della normativa applicabile e, quando richiesto,
              previo consenso dell'utente.
            </p>

            <p className="mt-3">
              Per maggiori informazioni sulle tecnologie utilizzate e sulla
              gestione dei cookie, consulta la nostra{" "}
              <Link
                to="/cookies"
                className="text-gold hover:underline"
              >
                Cookie Policy
              </Link>.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              13. Sicurezza
            </h2>

            <p>
              Mytharion adotta misure tecniche e organizzative adeguate,
              tenendo conto della natura, dell'ambito, del contesto e delle
              finalità del trattamento e dei rischi per i diritti e le
              libertà delle persone fisiche.
            </p>

            <p className="mt-3">
              Tali misure sono finalizzate a proteggere i dati personali
              da accessi non autorizzati, perdita, distruzione, alterazione,
              divulgazione o altri trattamenti non autorizzati.
            </p>

            <p className="mt-3">
              Nessun sistema informatico può tuttavia garantire una sicurezza
              assoluta. Gli utenti sono invitati a proteggere le proprie
              credenziali e a segnalare tempestivamente eventuali accessi
              non autorizzati.
            </p>
          </section>

          {/* 14 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              14. Decisioni Automatizzate e Profilazione
            </h2>

            <p>
              Mytharion non utilizza i dati personali degli utenti per
              effettuare decisioni basate esclusivamente su trattamenti
              automatizzati che producano effetti giuridici o incidano
              analogamente in modo significativo sull'utente ai sensi
              dell'articolo 22 del GDPR.
            </p>

            <p className="mt-3">
              Mytharion non effettua attività di profilazione degli utenti
              per finalità che producano gli effetti descritti dall'articolo
              22 del GDPR.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              15. Minori
            </h2>

            <p>
              Il Servizio non è destinato specificamente ai minori di 16
              anni e Mytharion non intende raccogliere consapevolmente dati
              personali di minori di tale età attraverso il Servizio in
              assenza delle condizioni previste dalla normativa applicabile.
            </p>

            <p className="mt-3">
              Qualora Mytharion venga a conoscenza di aver raccolto
              consapevolmente dati personali di un minore in violazione
              della normativa applicabile, adotterà misure ragionevoli per
              gestire la situazione e, ove necessario, cancellare tali dati.
            </p>
          </section>

          {/* 16 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              16. Dati relativi a Funzionalità Future
            </h2>

            <p>
              Mytharion potrà introdurre in futuro nuove funzionalità,
              comprese funzionalità relative a giochi di ruolo virtuali,
              campagne, personaggi, mappe, file o altri contenuti creati
              dagli utenti.
            </p>

            <p className="mt-3">
              Qualora tali funzionalità comportino nuovi trattamenti di
              dati personali, Mytharion aggiornerà la presente Privacy
              Policy prima o contestualmente all'introduzione dei relativi
              trattamenti, quando richiesto dalla normativa applicabile.
            </p>
          </section>

          {/* 17 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              17. Reclamo all'Autorità di Controllo
            </h2>

            <p>
              Se ritieni che il trattamento dei tuoi dati personali violi
              il GDPR o la normativa applicabile, hai il diritto di proporre
              un reclamo all'autorità di controllo competente, senza
              pregiudizio per ogni altro ricorso amministrativo o
              giurisdizionale previsto dalla legge.
            </p>

            <p className="mt-3">
              Per i trattamenti soggetti alla normativa italiana, l'autorità
              di controllo è il{" "}
              <strong>Garante per la protezione dei dati personali</strong>.
            </p>
          </section>

          {/* 18 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              18. Modifiche alla Privacy Policy
            </h2>

            <p>
              Mytharion può modificare o aggiornare periodicamente questa
              Privacy Policy, ad esempio per riflettere modifiche al
              Servizio, ai fornitori utilizzati, alle finalità del
              trattamento o alla normativa applicabile.
            </p>

            <p className="mt-3">
              In caso di modifiche sostanziali, Mytharion adotterà misure
              ragionevoli per informare gli utenti, quando richiesto dalla
              normativa applicabile.
            </p>

            <p className="mt-3">
              La versione aggiornata della Privacy Policy sarà pubblicata
              su questa pagina con l'indicazione della relativa data di
              aggiornamento.
            </p>
          </section>

          {/* 19 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              19. Contatti
            </h2>

            <p>
              Per domande relative alla presente Privacy Policy, al
              trattamento dei dati personali o per esercitare i diritti
              previsti dal GDPR, puoi contattarci tramite:
            </p>

            <p className="mt-3">
              <strong>Email:</strong>{" "}
              <a
                href="mailto:privacy@mytharion.app"
                className="text-gold hover:underline"
              >
                privacy@mytharion.app
              </a>
            </p>

            <p className="mt-2">
              <strong>Sede:</strong> Napoli, Italia
            </p>

            <p className="mt-3">
              Le richieste di cancellazione dell'account e dei dati
              personali possono inoltre essere effettuate tramite il
              sistema di ticket disponibile sul sito.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border">
          <Link
            to="/"
            className="text-gold hover:text-gold/80 text-sm"
          >
            ← Torna alla Home
          </Link>
        </div>

      </div>
    </div>
  );
};

export default PrivacyPage;